import React, { useEffect } from 'react';
import { Container } from '../components/ui/Container/Container';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';

export function TermsOfService() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      {/* Main Content */}
      <main className="py-16">
        <Container size="md">
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-foreground mb-4">Terms of Service</h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">1. Acceptance of Terms</h2>
                  <p className="text-muted-foreground mb-4">
                    Welcome to Moniker Cockpit ("we," "our," or "us"). These Terms of Service ("Terms") govern your use of our social media asset creation platform
                    and related services (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms.
                  </p>
                  <p className="text-muted-foreground">
                    If you do not agree to these Terms, please do not use our Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">2. Description of Service</h2>
                  <p className="text-muted-foreground mb-4">
                    Moniker Cockpit provides a company management and automation platform for streamlined operations and business workflows.
                    Our service includes management tools, AI-assisted features, automation capabilities, and integrated business solutions.
                  </p>
                  <p className="text-muted-foreground">
                    We reserve the right to modify, suspend, or discontinue any part of the Service at any time.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">3. User Accounts</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">3.1 Account Creation</h3>
                  <p className="text-muted-foreground mb-4">
                    To use certain features of our Service, you must create an account. You agree to:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your password</li>
                    <li>Be responsible for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized use</li>
                  </ul>

                  <h3 className="text-xl font-medium text-foreground mb-3">3.2 Account Termination</h3>
                  <p className="text-muted-foreground">
                    We reserve the right to suspend or terminate your account at any time for violations of these Terms or for other reasons we deem necessary.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">4. User Content and Conduct</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">4.1 User Content</h3>
                  <p className="text-muted-foreground mb-4">
                    You retain ownership of content you create using our Service ("User Content"). By using our Service, you grant us a license to:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Store and process your content to provide the Service</li>
                    <li>Display your content as necessary for the Service to function</li>
                    <li>Make technical copies as required for backup and security</li>
                  </ul>

                  <h3 className="text-xl font-medium text-foreground mb-3">4.2 Acceptable Use</h3>
                  <p className="text-muted-foreground mb-4">You agree not to use the Service to:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Create or distribute illegal, harmful, or offensive content</li>
                    <li>Violate intellectual property rights</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Interfere with the Service or other users' experience</li>
                    <li>Use automated tools or bots without permission</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">5. Intellectual Property</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">5.1 Our Intellectual Property</h3>
                  <p className="text-muted-foreground mb-4">
                    The Service and its original content, features, and functionality are owned by Moniker Cockpit and are protected by copyright,
                    trademark, and other intellectual property laws.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">5.2 User Content Rights</h3>
                  <p className="text-muted-foreground">
                    You are responsible for ensuring you have the right to use any content you upload to our Service.
                    You agree not to upload content that infringes on others' intellectual property rights.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">6. Payment and Billing</h2>
                  <p className="text-muted-foreground mb-4">
                    Some features of our Service may require payment. If you purchase a paid plan:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>You agree to pay all fees associated with your chosen plan</li>
                    <li>Payments are non-refundable except as required by law</li>
                    <li>We may change pricing with 30 days' notice</li>
                    <li>You are responsible for all applicable taxes</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">7. Privacy</h2>
                  <p className="text-muted-foreground">
                    Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy,
                    which is incorporated into these Terms by reference.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">8. Disclaimers and Limitations</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">8.1 Service Availability</h3>
                  <p className="text-muted-foreground mb-4">
                    We strive to provide reliable service but cannot guarantee uninterrupted or error-free operation.
                    The Service is provided "as is" without warranties of any kind.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">8.2 Limitation of Liability</h3>
                  <p className="text-muted-foreground">
                    To the maximum extent permitted by law, Moniker Cockpit shall not be liable for any indirect, incidental,
                    special, or consequential damages arising from your use of the Service.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">9. Indemnification</h2>
                  <p className="text-muted-foreground">
                    You agree to indemnify and hold Moniker Cockpit harmless from any claims, damages, or expenses
                    arising from your use of the Service or violation of these Terms.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">10. Termination</h2>
                  <p className="text-muted-foreground mb-4">
                    Either party may terminate this agreement at any time. Upon termination:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Your right to use the Service ceases immediately</li>
                    <li>We may delete your account and associated data</li>
                    <li>Sections that should survive termination will remain in effect</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">11. Governing Law</h2>
                  <p className="text-muted-foreground">
                    These Terms shall be governed by and construed in accordance with the laws of [Jurisdiction],
                    without regard to its conflict of law provisions.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">12. Changes to Terms</h2>
                  <p className="text-muted-foreground mb-4">
                    We reserve the right to modify these Terms at any time. We will notify users of material changes
                    via email or through our Service. Your continued use after changes constitutes acceptance.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">13. Contact Information</h2>
                  <p className="text-muted-foreground mb-4">
                    If you have questions about these Terms, please contact us:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-muted-foreground mb-2"><strong>Email:</strong> legal@saas-boilerplate.com</p>
                    <p className="text-muted-foreground mb-2"><strong>Address:</strong> [Company Address]</p>
                    <p className="text-muted-foreground"><strong>Phone:</strong> [Contact Number]</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </Container>
      </main>

      <PublicFooter />
    </div>
  );
}
